import { Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ConnectCandidateMatchCardDataTestIds } from 'data-testids/CandidatePortalDataTestIds';
import { formatDistance, getGradeRange } from 'features/Connect/features/JobDiscovery/utils';
import { ConnectPreferenceMatchesSectionInterface } from 'types/connectTypes';
import { PreferenceMatchBadge } from '../InfoBadges/PreferenceMatchBadge';
export const PreferenceMatchesSection: React.FC<ConnectPreferenceMatchesSectionInterface> = ({
  currentMatch,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const maxSubjects = isTablet ? 2 : 3;

  const { school, preference_matches: preferenceMatches } = currentMatch || {};
  const {
    grades_matched: gradesMatched = [],
    subcategories_matched: subcategoriesMatched = [],
    has_distance_preference_match: hasDistancePreferenceMatch,
  } = preferenceMatches || {};

  const getGradeMatchLabel = (): string => {
    const gradeMatchLabel =
      (gradesMatched.length > 1 ? 'Grades' : 'Grade') + ' ' + getGradeRange(gradesMatched);
    return gradeMatchLabel;
  };

  const getSubjectMatchLabel = (): string => {
    const firstSubjects = subcategoriesMatched.sort().slice(0, maxSubjects);
    if (subcategoriesMatched.length > maxSubjects) {
      const remainingCount = subcategoriesMatched.length - maxSubjects;
      return `${firstSubjects.join(', ')} + ${remainingCount} more`;
    } else {
      return firstSubjects.join(', ');
    }
  };

  return (
    <PreferenceMatchesSectionContainer
      data-testid={ConnectCandidateMatchCardDataTestIds.PREFERENCE_MATCHES_CONTAINER}
    >
      <PreferenceMatchesSectionTitle>What you matched on:</PreferenceMatchesSectionTitle>
      <PreferenceMatchesBadgeContainer>
        {gradesMatched.length > 0 && (
          <PreferenceMatchBadge
            preferenceMatch={getGradeMatchLabel()}
            dataTestId={`${ConnectCandidateMatchCardDataTestIds.PREFERENCE_MATCH_CHIP}-grades`}
          />
        )}
        {subcategoriesMatched.length > 0 && (
          <PreferenceMatchBadge
            preferenceMatch={getSubjectMatchLabel()}
            dataTestId={`${ConnectCandidateMatchCardDataTestIds.PREFERENCE_MATCH_CHIP}-subjects`}
          />
        )}
        {hasDistancePreferenceMatch && (
          <PreferenceMatchBadge
            preferenceMatch={`${formatDistance(school?.distance_from_search_location_miles)} miles away`}
            dataTestId={`${ConnectCandidateMatchCardDataTestIds.PREFERENCE_MATCH_CHIP}-distance`}
          />
        )}
      </PreferenceMatchesBadgeContainer>
    </PreferenceMatchesSectionContainer>
  );
};

const PreferenceMatchesSectionContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const PreferenceMatchesSectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.tertiary,
  fontSize: theme.typography.subtitle.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: theme.typography.h2.lineHeight,
}));

const PreferenceMatchesBadgeContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    '& > *': {
      width: '100%',
    },
  },
}));
